import { Index } from '../indexing/document-index'
import { DocumentIdIterator } from '../indexing/index-interface'

export type DocumentMatchIteratorWeight = {
  type: Symbol
  /**
   * The cost is an approximation of the number of documents that might need to be enumerated
   * and thus require testing
   */
  cost: number
  isSortable: boolean
}

export const IterateNoDocuments = Symbol('IterateOverNoDocuments')
export const IterateAllDocuments = Symbol('IterateOverAllDocuments')
export const IterateOverSubIterators = Symbol('IterateOverSubIterators')
export const IterateOverId = Symbol('IterateOverId')
export const IterateOverIndex = Symbol('IterateOverIndex')

export type DocumentMatchIteratorAllDocuments = DocumentMatchIteratorWeight

export type DocumentMatchIteratorUsingId = DocumentMatchIteratorWeight & {
  id: string
}

export type DocumentMatchIteratorUsingIndex = DocumentMatchIteratorWeight & {
  key: string
  index: Index
  idIterator: DocumentIdIterator
}

export type DocumentMatchIteratorOverNoDocuments = DocumentMatchIteratorUsingIndex

export type DocumentMatchIteratorJoined = DocumentMatchIteratorWeight & {
  operator: 'intersect' | 'union'
  iterators: (DocumentMatchIteratorUsingIndex | DocumentMatchIteratorJoined | DocumentMatchIteratorUsingId)[]
}

export const KMergedUnion = Symbol('KMergedUnion')
export const KMergedIntersection = Symbol('KMergedIntersection')

export interface KMergedUnionIterator extends DocumentMatchIteratorWeight {
  type: typeof KMergedUnion
  iterators: DocumentMatchIterator[] // Sub-iterators that can provide sorted DocumentIds
}

export interface KMergedIntersectionIterator extends DocumentMatchIteratorWeight {
  type: typeof KMergedIntersection
  iterators: DocumentMatchIterator[] // Sub-iterators that can provide sorted DocumentIds
}

export type DocumentMatchIterator =
  | DocumentMatchIteratorUsingId
  | DocumentMatchIteratorWeight
  | DocumentMatchIteratorUsingIndex
  | DocumentMatchIteratorJoined
  | KMergedUnionIterator
  | KMergedIntersectionIterator

export function describeIterator(iterator: any): string {
  if (iterator.type === IterateOverId) {
    return `IterateOverId(${iterator.id})`
  } else if (iterator.type === IterateOverIndex) {
    return `IterateOverIndex(${iterator.index.fieldPath} = ${iterator.key})`
  } else if (iterator.type === IterateAllDocuments) {
    return 'IterateAllDocuments'
  } else if (iterator.type === IterateNoDocuments) {
    return 'IterateNoDocuments'
  } else if (iterator.type === KMergedUnion) {
    return `KMergedUnion(${iterator.iterators.length} iterators)`
  } else if (iterator.type === KMergedIntersection) {
    return `KMergedIntersection(${iterator.iterators.length} iterators)`
  } else if (iterator.type === IterateOverSubIterators) {
    return `IterateOverSubIterators(${iterator.operator} [ ${iterator.iterators.map((i: any) => describeIterator(i)).join(', ')} ]) `
  }

  return 'Unknown iterator'
}

export function isEmptyIterator(iterator: DocumentMatchIteratorWeight): boolean {
  return iterator.cost === 0 || iterator.type === IterateNoDocuments
}

export function isIteratorOverAllDocuments(iterator: DocumentMatchIteratorWeight): boolean {
  return iterator.type === IterateAllDocuments || iterator.cost === Number.MAX_SAFE_INTEGER
}

export function isIteratorOverNoDocuments(iterator: DocumentMatchIteratorWeight): iterator is DocumentMatchIteratorOverNoDocuments {
  return iterator.type === IterateNoDocuments || iterator.cost === 0
}

export function isIteratorUsingId(iterator: DocumentMatchIteratorWeight): iterator is DocumentMatchIteratorUsingId {
  return iterator.type === IterateOverId && 'id' in iterator
}

export function isIteratorUsingIndexOfDocuments(iterator: DocumentMatchIteratorWeight): iterator is DocumentMatchIteratorUsingIndex {
  return iterator.type === IterateOverIndex
}

export function isIteratorUsingJoinsOfIterators(iterator: DocumentMatchIteratorWeight): iterator is DocumentMatchIteratorJoined {
  return iterator.type === IterateOverSubIterators
}
